import React, { PropsWithChildren, ReactElement, ReactNode, startTransition, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Switch } from 'ui/atoms'
import NewButton from 'ui/atoms/NewButton/NewButton'
import InfoBox from 'ui/components/ModuleHeader/InfoBox'
import { DatePickerPeriod } from 'ui/molecules/pickers/DatePicker/DatePickerPeriod'
import TimePeriodPicker from 'ui/molecules/TimePeriodPicker/TimePeriodPicker'
import Datetime from 'utils/datetime/datetime'
import styles from './EventsViewHeader.module.less'

type EventsViewHeaderProps = {
  infoHeader: string | React.JSX.Element
  onInfoButtonClick?: () => void
  beta?: boolean
  secondaryChildren?: ReactNode
  includePropertyFilter?: boolean
  initDatePeriod: 'productionPlanPeriod' | 'week' | 'month' | 'custom'
  datePeriod: { startTime: ISODateTime; endTime: ISODateTime }
  setDatePeriod: (startTime: ISODateTime, endTime: ISODateTime) => void
}

export default function EventsViewHeader({
  infoHeader,
  onInfoButtonClick,
  children,
  beta,
  secondaryChildren,
  initDatePeriod,
  datePeriod,
  setDatePeriod,
}: PropsWithChildren<EventsViewHeaderProps>): ReactElement {
  const { t } = useTranslation()

  const [switchValue, setSwitchValue] = useState(initDatePeriod)

  const [selectedDates, setSelectedDates] = useState({
    startTime: datePeriod.startTime,
    endTime: datePeriod.endTime,
  })

  const switchItems = [
    {
      value: `productionPlanPeriod`,
      label: t(`Production plan period`),
    },
    {
      value: `week`,
      label: t(`Week`),
    },
    {
      value: `month`,
      label: t(`Month`),
    },
    {
      value: `custom`,
      label: t(`Custom`),
    },
  ]

  useEffect(() => {
    if (switchValue === 'month') {
      const time = Datetime.getStartTimeEndTimeOfMonthByDate(Datetime.getISONow())
      setDatePeriod(time.startTime, time.endTime)
    } else if (switchValue === 'week') {
      const time = Datetime.getStartTimeEndTimeOfWeek()
      setDatePeriod(time.startTime, time.endTime)
    } else if (switchValue === 'productionPlanPeriod') {
      const startTimeProductionPlan = Datetime.getStartOfDay(Datetime.getISONow(0))
      const endTimeProductionPlan = Datetime.getEndOfDay(Datetime.getISONow(168))
      setDatePeriod(startTimeProductionPlan, endTimeProductionPlan)
    }
  }, [setDatePeriod, switchValue])

  useEffect(() => {
    setSelectedDates({
      startTime: datePeriod.startTime,
      endTime: datePeriod.endTime,
    })
  }, [datePeriod.startTime, datePeriod.endTime])

  return (
    <div className={styles.EventsViewHeader_Root}>
      <div className={styles.EventsViewHeader_RootRow}>
        <div className={styles.EventsViewHeader_InfoBoxContainer}>
          <InfoBox infoHeader={infoHeader} onInfoButtonClick={onInfoButtonClick} beta={beta} />
        </div>

        <div className={styles.EventsViewHeader_MiddleRow}>{children}</div>
      </div>

      <div className={styles.EventsViewHeader_SecondaryChildrenContainer}>{secondaryChildren}</div>

      <div className={styles.EventsViewHeader_SwitchRow}>
        <div className={styles.EventsViewHeader_Switch}>
          <div className="flex items-center">
            <Switch
              key={switchValue}
              value={switchValue}
              onClick={(value) => {
                startTransition(() => {
                  setSwitchValue(value)
                })
              }}
              items={switchItems}
            />
            {(switchValue === 'week' || switchValue === 'month') && (
              <TimePeriodPicker
                periodType={switchValue === 'week' ? 'week' : 'month'}
                startTime={datePeriod.startTime}
                setTimePeriod={(date, periodType) => {
                  if (date && periodType) {
                    const { startTime, endTime } = Datetime.getStartAndEndOfTimePeriod(
                      date,
                      periodType === 'week' ? 'week' : 'month'
                    )
                    setDatePeriod(startTime, endTime)
                  }
                }}
              />
            )}
          </div>
        </div>
        <div
          className={`${styles.EventsViewHeader_CustomDatePicker} ${switchValue === 'custom' ? styles.EventsViewHeader_CustomDatePickerVisible : ''}`}
        >
          {switchValue === 'custom' && (
            <>
              <DatePickerPeriod
                startDate={datePeriod.startTime}
                endDate={datePeriod.endTime}
                onSelect={(startTime, endTime) => {
                  setSelectedDates({
                    startTime: startTime?.toString() as ISODateTime,
                    endTime: endTime?.toString() as ISODateTime,
                  })
                }}
                minDate={Datetime.toISOString('2020-01-01T00:00:00+00:00')}
              />
              <NewButton
                label={t('Load period')}
                onClick={() => {
                  setDatePeriod(
                    selectedDates.startTime || datePeriod.startTime,
                    selectedDates.endTime || datePeriod.endTime
                  )
                }}
              />
            </>
          )}
        </div>
      </div>
    </div>
  )
}
